import {
  selectCurrentHotelId,
  selectCurrentHotelName,
  selectIsInternalAdmin
} from 'accessors'
import {
  PrimaryRegularButton,
  SecondaryRegularButton
} from 'components/ReusableComponents/Button/RegularButtons'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import Dialog from 'components/ReusableComponents/Dialog'
import FileIcon from 'components/ReusableComponents/FileIcon'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import CampaignSetup from 'features/CustomCampaigns/EditCampaign/CampaignSetup'
import CampaignStartConfirmation from 'features/CustomCampaigns/EditCampaign/CampaignStartConfirmation'
import MainPromo from 'features/CustomCampaigns/EditCampaign/MainPromo'
import MainTemplate from 'features/CustomCampaigns/EditCampaign/MainTemplate'
import {
  customCampaignStatusTypes as statusTypes,
  getCampaignStatusColor
} from 'features/CustomCampaigns/helpers'
import PromoUsageWarningDialog from 'features/ManageCampaigns/EditPromoDialog/PromoUsageWarningDialog'
import { TextPreview, TextPreviewBubble } from 'features/ManageCampaigns/TextPreview'
import {
  getExtensionFromUrl,
  getImgUrlFromCDN,
  isCampaignImageVisible
} from 'features/ManageCampaigns/utils/campaignHelpers'
import { mediaTags } from 'features/ManageCampaigns/utils/constants'
import useEsc from 'hooks/useEsc'
import usePlausible from 'hooks/usePlausible'
import usePromoValidation from 'hooks/usePromoValidaiton'
import html2canvas from 'html2canvas'
import moment from 'moment'
import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router'
import { Link } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'
import audienceApi from 'services/api/audience'
import campaignApi from 'services/api/campaign'

import { ReactComponent as CloseIcon } from './close.svg'
import styles from './EditCampaign.module.scss'

const steps = {
  mainTemplate: 'mainTemplate',
  mainPromo: 'mainPromo',
  campaignSetup: 'campaignSetup'
}

const urlRegex = /^(http|https)?:\/\/\S+/i

const Dot = ({ fill = 'gray' }) => (
  <svg height="8" width="7">
    <circle cx="3.5" cy="3.5" r="3.5" fill={fill} />
  </svg>
)

const mediaTag = mediaTags.campaign

const requiredTags = ['@[hotelName](__hotelName__)', '@[guestName](__guestName__)']

const requiredTagIds = ['__hotelName__', '__guestName__']

const EditCampaign = () => {
  const { id } = useParams()

  const [campaignData, setCampaignData] = useState()

  const [isLoadingCampaign, setIsLoadingCampaign] = useState(false)
  const [isLoadingAudience, setIsLoadingAudience] = useState(false)
  const [deliveryStartDate, setDeliveryStartDate] = useState()
  const [deliveryFinishDate, setDeliveryFinishDate] = useState()

  const [promotionCode, setPromotionCode] = useState()
  const [maxDiscountPercentage, setMaxDiscountPercentage] = useState()
  const [minNightsToStay, setMinNightsToStay] = useState()
  const [promoTagline, setPromoTagline] = useState('Sample promo taglne')

  const [hasPromoValidityDate, setHasPromoValidityDate] = useState(false)
  const [promoValidityStartDate, setPromoValidityStartDate] = useState()
  const [promoValidityEndDate, setPromoValidityEndDate] = useState()
  const [showPromoUsageWarning, setShowPromoUsageWarning] = useState(null)

  const [messageMedia, setMessageMedia] = useState()

  const [customDeliveryStatus, setCustomDeliveryStatus] = useState()

  const [customUrl, setCustomUrl] = useState()

  const [audiences, setAudiences] = useState([])

  const [canModifySetup, setCanModifySetup] = useState(false)

  const history = useHistory()

  const { postEvent, plausibleEvents } = usePlausible()

  const isInternalAdmin = useSelector(selectIsInternalAdmin)

  useEffect(() => {
    setIsLoadingCampaign(true)

    campaignApi
      .getCustomCampaignList()
      .then(res => {
        const campaign = res.items.find(c => c.contentId === Number(id))
        setCampaignData(campaign)
      })
      .catch(err => {
        console.warn(err)
      })
      .finally(() => {
        setIsLoadingCampaign(false)
      })
  }, [id, isInternalAdmin])

  useEffect(() => {
    setIsLoadingAudience(true)

    audienceApi
      .getList()
      .then(res => {
        setAudiences(res)
      })
      .catch(err => {
        console.warn(err)
      })
      .finally(() => {
        setIsLoadingAudience(false)
      })
  }, [])

  useEffect(() => {
    if (campaignData) {
      const c = campaignData

      setAudienceId(c.audienceId)
      setPromotionCode(c.promotionCode)
      setPromoTagline(c.promotionTagline)
      setMinNightsToStay(c.minNightsToStay)
      setMaxDiscountPercentage(c.maxDiscountPercentage)

      setDeliveryStartDate(c.deliveryStartDate || moment().add(1, 'days').toDate())
      setDeliveryFinishDate(c.deliveryFinishDate)

      setMessageTemplate(c.messageTemplate)
      setMessageMedia(c.messageMediaUrls?.[0])

      setUsedAllTags(requiredTags.every(tag => c.messageTemplate.includes(tag)))

      setCustomDeliveryStatus(c.customDeliveryStatus)

      setCustomUrl(c.customUrl)

      setHasPromoValidityDate(c.hasPromoValidityDate)
      setPromoValidityStartDate(
        c.promoValidityStartDate ? new Date(c.promoValidityStartDate) : new Date()
      )
      setPromoValidityEndDate(
        c.promoValidityEndDate
          ? new Date(c.promoValidityEndDate)
          : moment().add(15, 'day').toDate()
      )

      if ([statusTypes.Draft, statusTypes.Approved].includes(c.customDeliveryStatus)) {
        setCanModifySetup(true)
      }

      if (c.customDeliveryStatus === statusTypes.Approved) {
        setActiveStep(steps.mainTemplate)
      }
    }
  }, [campaignData])

  function updateCampaign(customDeliveryStatus) {
    postEvent(plausibleEvents.UpdateTextBlast, { id })

    campaignApi
      .updateCustomCampaign({
        ...campaignData,
        audienceId,
        deliveryStartDate: moment(deliveryStartDate).format('YYYY-MM-DD'),
        deliveryFinishDate: deliveryFinishDate
          ? moment(deliveryFinishDate).format('YYYY-MM-DD')
          : null,
        messageTemplate,
        messageMediaUrls: messageMedia ? [messageMedia] : [],
        customDeliveryStatus: customDeliveryStatus || campaignData.customDeliveryStatus,
        customUrl,
        promotionCode,
        promotionTagline: promoTagline,
        maxDiscountPercentage,
        minNightsToStay,
        hasPromoValidityDate,
        promoValidityStartDate: moment(promoValidityStartDate).format('YYYY-MM-DD'),
        promoValidityEndDate: moment(promoValidityEndDate).format('YYYY-MM-DD'),
        isActive:
          (customDeliveryStatus || campaignData.customDeliveryStatus) ===
          statusTypes.Draft
            ? false
            : true
      })
      .then(() => {
        history.push('/custom-campaigns')
      })
      .catch(err => {
        console.warn(err)
      })
  }

  const wrapperRef = useRef()

  useEsc(() => {
    if (wrapperRef.current.offsetParent !== null) {
      history.push('/custom-campaigns')
    }
  })

  const { addToast } = useToasts()

  const userName = useSelector(s => s.login.currentUser.name)
  const hotelName = useSelector(selectCurrentHotelName)
  const hotelId = useSelector(selectCurrentHotelId)
  const isHotelSetupCompleted = useSelector(s => s.hotelDetails.isPreOnboardingCompleted)

  const [messageTemplate, setMessageTemplate] = useState('')

  const [usedAllTags, setUsedAllTags] = useState(true)

  const [audienceId, setAudienceId] = useState()

  function handleTextChange(text) {
    setMessageTemplate(text.replace(/inviteLink__\)(\S)/, 'inviteLink__) $1'))
  }

  const [isConfirmationVisible, setIsConfirmationVisible] = useState()

  const stepNames = {
    mainTemplate: 'Text content',
    mainPromo: 'Promotion',
    campaignSetup: 'Audience & dates',
    optInPromo: 'Opt-in promotion'
  }

  const [activeStep, setActiveStep] = useState(steps.mainTemplate)

  const [nextStep, setNextStep] = useState()
  const [isNextDisabled, setIsNextDisabled] = useState(true)

  const [isValidated, setIsValidated] = useState()

  useEffect(() => {
    const isSetupGood = deliveryStartDate || !canModifySetup
    const isMainTemplateGood = usedAllTags

    setIsValidated(isSetupGood && isMainTemplateGood)
  }, [deliveryFinishDate, deliveryStartDate, usedAllTags, activeStep, canModifySetup])

  useEffect(() => {
    let isGood

    if (activeStep === steps.campaignSetup) {
      isGood = (deliveryStartDate && deliveryFinishDate) || !canModifySetup
    }

    if (activeStep === steps.mainTemplate) {
      isGood = usedAllTags && (customUrl ? !!customUrl.match(urlRegex) : true)
    }

    if (activeStep === steps.mainPromo) {
      isGood = true
    }

    setIsNextDisabled(!isGood)
  }, [
    deliveryFinishDate,
    deliveryStartDate,
    usedAllTags,
    activeStep,
    canModifySetup,
    customUrl
  ])

  useEffect(() => {
    function getNextStep() {
      switch (activeStep) {
        case steps.mainTemplate:
          return steps.mainPromo

        case steps.mainPromo:
          return steps.campaignSetup

        case steps.campaignSetup:
          return null

        default:
          return null
      }
    }

    setNextStep(getNextStep())
  }, [activeStep])

  const [isMissingMediaWarningVisible, setIsMissingMediaWarningVisible] = useState(false)

  async function startCampaign() {
    postEvent(plausibleEvents.StartTextBlast, { id })

    setIsMissingMediaWarningVisible(false)

    setTimeout(() => {
      html2canvas(document.querySelector('#text-blast-estimation'))
        .then(canvas =>
          campaignApi.postCampaignEstimationScreenshot({
            contentId: id,
            screenshot: canvas.toDataURL('image/png', 1)
          })
        )
        .then(() => {
          updateCampaign(statusTypes.Approved)
        })
    }, 100)
  }

  async function checkMediaAndStartCampaign() {
    if (!isHotelSetupCompleted) {
      addToast(
        'Your hotel setup is not completed yet. Complete your setup to start text blasts.',
        {
          appearance: 'error',
          autoDismiss: true
        }
      )
      return
    }

    if (messageMedia) {
      startCampaign()
    } else {
      setIsMissingMediaWarningVisible(true)
    }
  }

  const { checkRateCodeUsage } = usePromoValidation(promotionCode)

  async function handleNext() {
    if (activeStep === steps.mainPromo && promotionCode) {
      const { messageType } = await checkRateCodeUsage(promotionCode)

      if (!messageType) {
        setActiveStep(nextStep)
      }

      setShowPromoUsageWarning(messageType)
    } else {
      setActiveStep(nextStep)
    }
  }

  if (showPromoUsageWarning) {
    return (
      <PromoUsageWarningDialog
        softMessage={true}
        promoCode={promotionCode}
        onClose={() => setShowPromoUsageWarning(false)}
        onConfirm={promotionCode}
        showPromoUsageWarning={showPromoUsageWarning}
      />
    )
  }

  if (isLoadingCampaign || isLoadingAudience || !messageTemplate) {
    return <LoadingIndicator />
  }

  if (isMissingMediaWarningVisible) {
    return (
      <ConfirmationDialog
        title="Do you want to start the campaign without a media?"
        onConfirm={() => startCampaign()}
        onClose={() => {
          setIsMissingMediaWarningVisible(false)
          setActiveStep(steps.mainTemplate)
        }}
        positiveMessage="Yes, start without media"
        negativeMessage="No, go back"
        hasDangerousAction
      >
        <p>
          Adding an image or video is <strong>strongly recommended</strong> to get the
          best results from text blasts.
        </p>
      </ConfirmationDialog>
    )
  }

  if (isConfirmationVisible) {
    return (
      <CampaignStartConfirmation
        startDate={deliveryStartDate}
        deliveryName={campaignData?.deliveryName}
        handleConfirm={() => startCampaign()}
        handleCancel={() => setIsConfirmationVisible(false)}
        audienceId={audienceId}
      />
    )
  }

  // function saveAs(uri, filename) {
  //   var link = document.createElement('a')

  //   if (typeof link.download === 'string') {
  //     link.href = uri
  //     link.download = filename

  //     //Firefox requires the link to be in the body
  //     document.body.appendChild(link)

  //     //simulate click
  //     link.click()

  //     //remove the link when done
  //     document.body.removeChild(link)
  //   } else {
  //     window.open(uri)
  //   }
  // }

  return (
    <Dialog>
      <div className={styles.resolutionWrapper} ref={wrapperRef}>
        <div className={styles.dialogWrapper}>
          <div className={styles.mainContent}>
            <div className={styles.navigationSidebar}>
              <div>
                <div className={styles.dialogDesc}>
                  <span className={styles.deliveryName}>
                    {campaignData?.deliveryName.replace('(', '\n(')}
                  </span>
                  <p
                    className={styles.status}
                    style={{ color: getCampaignStatusColor(customDeliveryStatus) }}
                  >
                    <span className={styles.dot}>
                      <Dot fill={getCampaignStatusColor(customDeliveryStatus)} />
                    </span>
                    {customDeliveryStatus}
                  </p>
                </div>
              </div>
              <div className={styles.controls}>
                <div className={styles.steps}>
                  {Object.values(steps).map(step => (
                    <button
                      key={step}
                      onClick={() => setActiveStep(step)}
                      className={step === activeStep ? styles.activeStep : styles.step}
                    >
                      <span className={styles.paginatorNo}>{stepNames[step]}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className={styles.editTextHolder}>
              <Link className={styles.closeButton} to="/custom-campaigns">
                <CloseIcon />
              </Link>
              <div className={styles.editTextContent}>
                {activeStep === steps.campaignSetup && (
                  <CampaignSetup
                    audienceId={audienceId}
                    audiences={audiences}
                    isEdibable={canModifySetup}
                    deliveryStartDate={deliveryStartDate}
                    setDeliveryStartDate={setDeliveryStartDate}
                    setDeliveryFinishDate={setDeliveryFinishDate}
                    deliveryFinishDate={deliveryFinishDate}
                    setAudienceId={setAudienceId}
                  />
                )}
                {activeStep === steps.mainTemplate && (
                  <MainTemplate
                    messageTemplate={messageTemplate}
                    onTextChange={handleTextChange}
                    usedAllTags={usedAllTags}
                    setUsedAllTags={setUsedAllTags}
                    setMessageMedia={setMessageMedia}
                    messageMedia={messageMedia}
                    isAllTagsRequired={true}
                    mediaTag={mediaTag}
                    requiredTagIds={requiredTagIds}
                    isMediaRequired={true}
                    customUrl={customUrl}
                    setCustomUrl={setCustomUrl}
                    isCustomUrlAllowed={true}
                  />
                )}
                {activeStep === steps.mainPromo && (
                  <MainPromo
                    promotionCode={promotionCode}
                    setPromotionCode={setPromotionCode}
                    minNightsToStay={minNightsToStay}
                    setMinNightsToStay={setMinNightsToStay}
                    maxDiscountPercentage={maxDiscountPercentage}
                    setMaxDiscountPercentage={setMaxDiscountPercentage}
                    setPromoTagline={setPromoTagline}
                    promoTagline={promoTagline}
                    hasPromoValidityDate={hasPromoValidityDate}
                    setHasPromoValidityDate={setHasPromoValidityDate}
                    promoValidityStartDate={promoValidityStartDate}
                    setPromoValidityStartDate={setPromoValidityStartDate}
                    promoValidityEndDate={promoValidityEndDate}
                    setPromoValidityEndDate={setPromoValidityEndDate}
                  />
                )}
              </div>
            </div>
          </div>
          <div className={styles.submitHolder}>
            {customDeliveryStatus === statusTypes.Draft && (
              <>
                <SecondaryRegularButton onClick={() => updateCampaign(statusTypes.Draft)}>
                  Save for later
                </SecondaryRegularButton>
                {nextStep ? (
                  <PrimaryRegularButton
                    className={styles.nextButton}
                    onClick={handleNext}
                    disabled={isNextDisabled}
                  >
                    Next
                  </PrimaryRegularButton>
                ) : (
                  <PrimaryRegularButton
                    disabled={!isValidated}
                    onClick={checkMediaAndStartCampaign}
                  >
                    Start campaign
                  </PrimaryRegularButton>
                )}
              </>
            )}
            {customDeliveryStatus === statusTypes.Approved && (
              <>
                <SecondaryRegularButton onClick={() => updateCampaign(statusTypes.Draft)}>
                  Convert to draft
                </SecondaryRegularButton>
                <PrimaryRegularButton
                  disabled={!isValidated}
                  onClick={() => updateCampaign()}
                >
                  Save campaign
                </PrimaryRegularButton>
              </>
            )}
            {customDeliveryStatus === statusTypes.Started && (
              <>
                <SecondaryRegularButton
                  onClick={() => updateCampaign(statusTypes.Interrupted)}
                >
                  Abort campaign
                </SecondaryRegularButton>
                <PrimaryRegularButton
                  disabled={!isValidated}
                  onClick={() => updateCampaign()}
                >
                  Save campaign
                </PrimaryRegularButton>
              </>
            )}
          </div>
        </div>
        <div className={styles.previewHolder}>
          <TextPreview>
            <>
              <TextPreviewBubble
                defaultData={{ userName, hotelName }}
                hasLink
                text={`${messageTemplate}`}
              />
              {messageMedia && isCampaignImageVisible(messageMedia) && (
                <img
                  src={getImgUrlFromCDN(messageMedia, mediaTag, hotelId)}
                  alt="campaign media"
                  className={styles.campaignMedia}
                />
              )}
              {messageMedia && getExtensionFromUrl(messageMedia) === 'pdf' && (
                <div className={styles.campaignFile}>
                  <FileIcon extension="pdf" />
                  <span />
                </div>
              )}
              <TextPreviewBubble defaultData={{ userName, hotelName }} />
            </>
          </TextPreview>
        </div>
      </div>
    </Dialog>
  )
}

export default EditCampaign
